import { reverseSegments, sampleSegment } from "./geometry";
import type { DraftBlock, DraftResult, DraftSegment, PointTuple } from "./types";

export type PaperSize = "A4" | "A3";
export const PAPER_SIZES: Record<PaperSize, { width: number; height: number }> = { A4: { width: 210, height: 297 }, A3: { width: 297, height: 420 } };

/** Printed grid spacing, in mm: sheets advance by whole grid squares so the grid runs on unbroken from sheet to sheet. */
export const PRINT_GRID = 10;
const MIN_MARGIN = 10;
const OVERLAP = 10;

export interface PrintSheet {
  paper: PaperSize;
  pageWidth: number;
  pageHeight: number;
  /** Left and top edge of the printable area on the page. */
  marginX: number;
  marginY: number;
  /** Printable area, overlap included. */
  width: number;
  height: number;
  overlap: number;
  /** How far each sheet advances on its neighbour: printable size less the overlap. */
  stepX: number;
  stepY: number;
}

/**
 * The printable area of a portrait sheet, centred on the page. It is the paper
 * less 10 mm margins, cut down so each step between sheets is a whole number of
 * grid squares: otherwise the grid on the next sheet would land part-way between
 * this sheet's lines and the overlap would not line up.
 */
export function printSheet(paper: PaperSize): PrintSheet {
  const page = PAPER_SIZES[paper];
  const step = (length: number) => Math.floor((length - 2 * MIN_MARGIN - OVERLAP) / PRINT_GRID) * PRINT_GRID;
  const stepX = step(page.width);
  const stepY = step(page.height);
  const width = stepX + OVERLAP;
  const height = stepY + OVERLAP;
  return { paper, pageWidth: page.width, pageHeight: page.height, marginX: (page.width - width) / 2, marginY: (page.height - height) / 2, width, height, overlap: OVERLAP, stepX, stepY };
}

/** The default A4 sheet. */
export const PRINT_SHEET = printSheet("A4");
const GAP = 20;

/** Sheets needed to cover `length` mm when sheets advance by `step` and overlap. */
const span = (length: number, step: number, overlap: number) => Math.max(1, Math.ceil(Math.max(0, length - overlap) / step));

export interface PrintRect { x: number; y: number; width: number; height: number }

/** Where a piece lands on the tiled print drawing, after its print rotation. */
export function printBounds(block: Pick<DraftBlock, "bounds" | "printOffset" | "printRotation">): PrintRect {
  const { bounds, printOffset: [dx, dy] } = block;
  // rotate(90) maps (x, y) to (-y, x).
  if (block.printRotation === 90) return { x: dx - bounds.maxY, y: dy + bounds.minX, width: bounds.height, height: bounds.width };
  return { x: dx + bounds.minX, y: dy + bounds.minY, width: bounds.width, height: bounds.height };
}

/** A sheet whose pieces cover less than this share of it is not printed, unless a piece would then not print at all. */
export const MIN_SHEET_COVERAGE = 0.05;

/** Joins edges end to end into a closed loop, in whatever order they come. Undefined if they do not join up. */
function outlineLoop(edges: DraftSegment[][]): DraftSegment[] | undefined {
  const same = (a: PointTuple, b: PointTuple) => Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.01;
  const rest = edges.slice(1);
  const loop = [...(edges[0] ?? [])];
  while (rest.length > 0) {
    const end = loop[loop.length - 1].end;
    const index = rest.findIndex((segments) => same(end, segments[0].start) || same(end, segments[segments.length - 1].end));
    if (index < 0) return undefined;
    const [segments] = rest.splice(index, 1);
    loop.push(...(same(end, segments[0].start) ? segments : reverseSegments(segments)));
  }
  return loop.length > 0 && same(loop[loop.length - 1].end, loop[0].start) ? loop : undefined;
}

/**
 * What a piece covers on the print drawing, as polygons: its outline and its
 * seam allowance and inlay strips. A piece whose outline does not close is
 * taken as its whole bounding box.
 */
export function printShapes(block: DraftBlock, draft: Pick<DraftResult, "edges" | "seamAllowances" | "inlays">): PointTuple[][] {
  const [dx, dy] = block.printOffset;
  // rotate(90) maps (x, y) to (-y, x).
  const place = ([x, y]: PointTuple): PointTuple => block.printRotation === 90 ? [dx - y, dy + x] : [dx + x, dy + y];
  const loop = outlineLoop(draft.edges.filter((edge) => block.edgeIds.includes(edge.id)).map((edge) => edge.segments));
  const { minX, minY, maxX, maxY } = block.bounds;
  const outline: PointTuple[] = loop ? loop.flatMap((segment) => sampleSegment(segment).slice(1)) : [[minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY]];
  const strips = [...draft.seamAllowances.filter((item) => block.seamAllowanceIds.includes(item.id)), ...draft.inlays.filter((item) => block.inlayIds.includes(item.id))].map((item) => item.polygon);
  return [outline, ...strips].map((polygon) => polygon.map(place));
}

function inside([x, y]: PointTuple, polygon: PointTuple[]): boolean {
  let within = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) within = !within;
  }
  return within;
}

/** The share of `rect` the shapes cover, estimated at the centres of `spacing` mm squares. */
export function printCoverage(shapes: PointTuple[][], rect: PrintRect, spacing = 5): number {
  const near = shapes.filter((shape) => {
    const xs = shape.map(([x]) => x), ys = shape.map(([, y]) => y);
    return Math.min(...xs) < rect.x + rect.width && Math.max(...xs) > rect.x && Math.min(...ys) < rect.y + rect.height && Math.max(...ys) > rect.y;
  });
  const columns = Math.max(1, Math.round(rect.width / spacing)), rows = Math.max(1, Math.round(rect.height / spacing));
  if (near.length === 0) return 0;
  let covered = 0;
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const point: PointTuple = [rect.x + ((column + 0.5) * rect.width) / columns, rect.y + ((row + 0.5) * rect.height) / rows];
      if (near.some((shape) => inside(point, shape))) covered++;
    }
  }
  return covered / (columns * rows);
}

/** Moves a piece that fits on one sheet off a sheet edge, onto the next sheet. */
function keepOnOneSheet(start: number, size: number, step: number, content: number): number {
  if (size > content) return start;
  const sheet = Math.floor(start / step);
  return start + size > sheet * step + content ? (sheet + 1) * step : start;
}

/** A free rectangle filled with shelves of small pieces, left to right then down. */
class Region {
  private cursorX: number;
  private shelfY: number;
  private shelfHeight = 0;
  constructor(private sheet: PrintSheet, private left: number, top: number, private right: number, private bottom: number) {
    this.cursorX = left;
    this.shelfY = top;
  }

  place(width: number, height: number): { x: number; y: number } | undefined {
    const { stepX, stepY, width: sheetWidth, height: sheetHeight } = this.sheet;
    let x = keepOnOneSheet(this.cursorX, width, stepX, sheetWidth);
    let y = this.shelfY;
    if (x + width > this.right || y + height > this.bottom) {
      if (this.shelfHeight === 0) return undefined;
      y = keepOnOneSheet(this.shelfY + this.shelfHeight + GAP, height, stepY, sheetHeight);
      x = keepOnOneSheet(this.left, width, stepX, sheetWidth);
      if (x + width > this.right || y + height > this.bottom) return undefined;
      this.shelfY = y;
      this.shelfHeight = 0;
    }
    this.cursorX = x + width + GAP;
    this.shelfHeight = Math.max(this.shelfHeight, height);
    return { x, y };
  }
}

/**
 * Lays pieces out for printing on tiled sheets (A4 unless told otherwise), setting each block's
 * printOffset and printRotation.
 *
 * A piece turns 90° when that makes it fewer sheets wide without adding sheets
 * (a waistband stood on end fits one column). Pieces with more area than a sheet
 * are major: each starts its own row of sheets, in pattern order, so one
 * piece's sheets tape together on their own. Small pieces fill the space beside
 * and below the major pieces, kept on a single sheet where they fit; any left
 * over share rows at the end.
 */
export function layoutPrint(blocks: DraftBlock[], sheet: PrintSheet = PRINT_SHEET): void {
  const { stepX, stepY, overlap } = sheet;
  const across = (length: number) => span(length, stepX, overlap);
  const down = (length: number) => span(length, stepY, overlap);
  const pieces = blocks.map((block) => {
    const { width, height } = block.bounds;
    // Turn only to stand a piece up narrower, never onto more sheets: the strip
    // of sheets stays as narrow as the widest piece and grows downwards.
    const turn = across(height) < across(width) && across(height) * down(width) <= across(width) * down(height);
    block.printRotation = turn ? 90 : 0;
    return { block, width: turn ? height : width, height: turn ? width : height };
  });
  // Major pieces cover more than a sheet; a pocket bag a little wider than a
  // sheet is still small enough to share a row.
  const isMajor = (piece: { width: number; height: number }) => piece.width * piece.height > sheet.width * sheet.height;
  const stripRight = Math.max(1, ...pieces.map((piece) => across(piece.width))) * stepX + overlap;

  const put = (block: DraftBlock, x: number, y: number) => {
    const { minX, minY, maxY } = block.bounds;
    block.printOffset = block.printRotation === 90 ? [x + maxY, y - minX] : [x - minX, y - minY];
  };

  const regions: Region[] = [];
  let rowTop = 0;
  for (const piece of pieces.filter(isMajor)) {
    put(piece.block, 0, rowTop);
    // Rows end on a sheet boundary with a gap to spare, so the next row's
    // pieces never share this row's sheets.
    const rowBottom = rowTop + Math.ceil((piece.height + GAP) / stepY) * stepY - GAP;
    regions.push(new Region(sheet, piece.width + GAP, rowTop, stripRight, rowBottom));
    if (piece.height + GAP < rowBottom - rowTop) regions.push(new Region(sheet, 0, rowTop + piece.height + GAP, piece.width, rowBottom));
    rowTop = rowBottom + GAP;
  }

  const leftovers = new Region(sheet, 0, rowTop, stripRight, Infinity);
  const minors = pieces.filter((piece) => !isMajor(piece)).sort((a, b) => b.height - a.height || b.width - a.width);
  for (const piece of minors) {
    const spot = regions.reduce<{ x: number; y: number } | undefined>((found, region) => found ?? region.place(piece.width, piece.height), undefined)
      ?? leftovers.place(piece.width, piece.height)!;
    put(piece.block, spot.x, spot.y);
  }
}
